/**
 * Canned responses — Step 21. Any agent manages these (not admin-gated
 * server-side), but they live under the settings shell alongside the other
 * "shared team config" pages rather than cluttering the main nav.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { cannedResponsesApi, type CannedResponse } from '../../features/tickets/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RichTextEditor } from '../../components/ui/richtext';
import { Modal } from '../../components/ui/modal';
import { Skeleton } from '../../components/ui/skeleton';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { useConfirm } from '../../lib/confirm';

export function CannedResponsesPage() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.cannedResponses,
    queryFn: cannedResponsesApi.list,
  });
  const [editing, setEditing] = useState<CannedResponse | 'new' | null>(null);
  const [folder, setFolder] = useState('');
  const [title, setTitle] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.cannedResponses });

  function openNew() {
    setEditing('new');
    setFolder('');
    setTitle('');
    setBodyHtml('');
  }
  function openEdit(c: CannedResponse) {
    setEditing(c);
    setFolder(c.folder ?? '');
    setTitle(c.title);
    setBodyHtml(c.bodyHtml);
  }

  const save = useMutation({
    mutationFn: () => {
      const body = { ...(folder.trim() ? { folder: folder.trim() } : {}), title: title.trim(), bodyHtml };
      return editing === 'new'
        ? cannedResponsesApi.create(body)
        : cannedResponsesApi.update(editing!.id, body);
    },
    onSuccess: (res) => {
      if (res.ok) {
        setEditing(null);
        invalidate();
      }
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => cannedResponsesApi.remove(id),
    onSuccess: () => invalidate(),
  });

  /**
   * Dirty against the record being edited — a Save that lights up on an
   * untouched form is a lie about state, and people stop trusting it. On
   * 'new' there is nothing to compare against, so content alone counts.
   *
   * ABOVE the loading and error returns: it sat below them, so the first
   * render ran fewer hooks than the second and this page would have thrown
   * React #310 on a cold load, exactly as `/admin/fields` did. Found by
   * `react-hooks/rules-of-hooks` the moment that rule was switched on, which
   * is the whole argument for switching it on.
   */
  const dirty =
    editing === 'new'
      ? !!title.trim() || !!bodyHtml.trim()
      : !!editing && (title !== editing.title || bodyHtml !== editing.bodyHtml);
  useDirty(dirty && editing !== null);

  if (isPending)
    return (
      <div className="max-w-2xl space-y-3 p-6">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (isError)
    return (
      <div className="m-6 max-w-2xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load canned responses</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  return (
    <div className="max-w-2xl p-6">
      <div className="mb-1 flex items-center gap-3">
        <FileText className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Canned responses</h1>
        <div className="flex-1" />
        <Button size="sm" onClick={openNew}>
          <Plus /> New
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Shared across every agent. Picked from the composer via "Canned response".
      </p>

      <div className="divide-y rounded-lg border bg-card">
        {data!.entries.map((c) => (
          <div key={c.id} className="flex items-center gap-2 px-3 py-2.5 text-sm">
            <span className="min-w-0 flex-1 truncate">
              {c.folder ? <span className="text-muted-foreground">{c.folder} / </span> : null}
              {c.title}
            </span>
            <Button variant="ghost" size="icon" aria-label={`Edit ${c.title}`} onClick={() => openEdit(c)}>
              <Pencil className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Delete ${c.title}`}
              onClick={async () => {
                if (
                  await confirm({ title: `Delete “${c.title}”?`, destructive: true, confirmLabel: 'Delete' })
                )
                  remove.mutate(c.id);
              }}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        ))}
        {data!.entries.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">
            No canned responses yet — create the first one above.
          </p>
        )}
      </div>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'New canned response' : 'Edit canned response'}
        wide
      >
        <div className="space-y-3 p-4">
          <div className="flex gap-2">
            <Input
              placeholder="Folder (optional)"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              className="w-40"
            />
            <Input
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="flex-1"
            />
          </div>
          <RichTextEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Response text…" />
          <p className="text-[11px] text-muted-foreground">
            Placeholders: {'{{ticket.ref}}'}, {'{{ticket.subject}}'}, {'{{requester.name}}'},{' '}
            {'{{requester.firstName}}'}, {'{{agent.name}}'} — filled in when inserted into a reply.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              disabled={!dirty || !title.trim() || !bodyHtml.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
